import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate } from "../lib/api";
import { Button } from "./ui/button";
import { Coins } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = {
  purchase: "Purchase", deduct: "Bid", restore: "Restored", expire: "Expired",
  promo: "Promotional",
};

function PassesTable({ passes, credits }) {
  if (!passes.length && !credits.length) return null;
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-x-auto bg-white dark:bg-slate-900">
      <table className="w-full text-sm min-w-[520px]" data-testid="bidz-passes-table">
        <thead className="bg-slate-50 dark:bg-slate-800 text-left text-xs uppercase text-slate-500">
          <tr><th className="px-4 py-3">Auction Pass</th><th className="px-4 py-3">Paid</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Bought</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
          {credits.map(c => (
            <tr key={c.id} data-testid={`bidz-pass-credit-${c.id}`}>
              <td className="px-4 py-3 font-semibold">Replacement pass credit <span className="text-xs text-slate-500 font-normal">(from {c.source_listing_title})</span></td>
              <td className="px-4 py-3">up to ₹{c.value}</td>
              <td className="px-4 py-3 text-emerald-600 font-bold uppercase text-xs">usable until {fmtDate(c.expires_at)}</td>
              <td className="px-4 py-3">{fmtDate(c.created_at)}</td>
            </tr>
          ))}
          {passes.map(p => (
            <tr key={p.id}>
              <td className="px-4 py-3"><Link className="text-amber-600 font-semibold" to={`/auction/${p.listing_id}`}>{p.listing_title}</Link></td>
              <td className="px-4 py-3">{p.price ? `₹${p.price}` : "Pass credit"}</td>
              <td className={`px-4 py-3 font-bold uppercase text-xs ${p.status === "active" ? "text-emerald-600" : "text-slate-500"}`}>{p.status}</td>
              <td className="px-4 py-3">{fmtDate(p.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-extrabold" data-testid={testid}>{value}</div>
    </div>
  );
}

function LotsTable({ lots }) {
  if (!lots.length) return null;
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-x-auto bg-white dark:bg-slate-900">
      <table className="w-full text-sm min-w-[520px]" data-testid="bidz-lots-table">
        <thead className="bg-slate-50 dark:bg-slate-800 text-left text-xs uppercase text-slate-500">
          <tr><th className="px-4 py-3">Source</th><th className="px-4 py-3">Remaining</th><th className="px-4 py-3">Usable on</th><th className="px-4 py-3">Expires</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
          {lots.map(l => (
            <tr key={l.id}>
              <td className="px-4 py-3 capitalize font-semibold">{l.source}</td>
              <td className="px-4 py-3 font-bold">{l.bidz_remaining} / {l.bidz_total}</td>
              <td className="px-4 py-3">Any auction</td>
              <td className="px-4 py-3">{fmtDate(l.expires_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HistoryTable({ txns }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-x-auto bg-white dark:bg-slate-900">
      <table className="w-full text-sm min-w-[560px]" data-testid="bidz-history-table">
        <thead className="bg-slate-50 dark:bg-slate-800 text-left text-xs uppercase text-slate-500">
          <tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Details</th><th className="px-4 py-3 text-right">Bidz</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
          {txns.map(t => (
            <tr key={t.id} data-testid={`bidz-txn-${t.id}`}>
              <td className="px-4 py-3 whitespace-nowrap">{new Date(t.created_at).toLocaleString("en-IN")}</td>
              <td className="px-4 py-3 font-semibold">{TYPE_LABEL[t.type] || t.type}</td>
              <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{t.reason}{t.listing_title ? ` · ${t.listing_title}` : ""}</td>
              <td className={`px-4 py-3 text-right font-bold ${t.bidz < 0 ? "text-red-600" : "text-emerald-600"}`}>{t.bidz > 0 ? `+${t.bidz}` : t.bidz}</td>
            </tr>
          ))}
          {txns.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-500">No Bidz activity yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export function BidzWallet() {
  const [w, setW] = useState(null);
  const [txns, setTxns] = useState([]);
  useEffect(() => {
    api.get("/bidz/wallet").then(r => setW(r.data)).catch(() => toast.error("Could not load your Bidz wallet"));
    api.get("/bidz/transactions").then(r => setTxns(r.data)).catch(() => setTxns([]));
  }, []);
  if (!w) return <div className="py-10 text-center text-slate-500">Loading wallet…</div>;
  const low = w.general_balance <= w.low_threshold;
  return (
    <div className="space-y-6" data-testid="bidz-wallet">
      {low && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="bidz-low-banner">
          <div className="text-sm font-semibold text-amber-900 dark:text-amber-200">Your Bidz are running low ({w.general_balance} left). Top up to keep bidding.</div>
          <Link to="/bidz"><Button size="sm" className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold" data-testid="bidz-low-topup-btn">Top up now</Button></Link>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="Wallet Bidz" value={w.general_balance} testid="bidz-wallet-general" />
        <Stat label="Active auction passes" value={w.active_pass_count} testid="bidz-wallet-pass" />
        <Stat label="Next expiry" value={fmtDate(w.next_expiry)} testid="bidz-wallet-next-expiry" />
      </div>
      <Link to="/bidz" className="inline-block pt-2"><Button className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold" data-testid="bidz-wallet-buy-btn"><Coins className="h-4 w-4 mr-2" /> Buy Bidz</Button></Link>
      <LotsTable lots={w.active_lots} />
      <PassesTable passes={w.passes} credits={w.pass_credits} />
      <h3 className="text-lg font-bold pt-2">Bidz history</h3>
      <HistoryTable txns={txns} />
    </div>
  );
}
